import { AbstractGameController } from "@lib/titan2/dist/game/controller/AbstractGameController";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { ArrayUtil } from "@lib/titan2/dist/util/collection/ArrayUtil";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { Combination } from "@lib/titan2/dist/util/math/Combination";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { Pattern } from "../../api/model/data/Pattern";
import { AvailableWinData } from "../model/data/AvailableWinData";
import { SlingoEvent } from "./event/SlingoEvent";
/**
 * WinController.ts
 */
export class WinController extends AbstractGameController {
    createModel() {
        super.createModel();
        this.model = this.baseModel;
    }
    create() {
        super.create();
        this.createPatterns();
        this.createUpdateWinTimer();
        this.createClearWinTimer();
        Logger.addObjectRef(this, "WinController");
    }
    createPatterns() {
        let ticket = this.model.getTicket();
        let rows = ticket.getNumberOfRows();
        let columns = ticket.getNumberOfColumns();
        let payoutConfig = this.model.getPayoutConfig();
        let patternSets = payoutConfig.getPatterns();
        let totalPatterns = patternSets.length;
        this.patterns = new Dictionary();
        let patternSetCombination;
        for (let i = 1; i <= patternSets.length; i++) {
            let combination = new Combination(totalPatterns, i);
            patternSetCombination = [];
            let patternSet;
            while (combination.hasNext()) {
                patternSet = new Pattern(rows, columns);
                let indexes = combination.next();
                for (let i = 0; i < indexes.length; i++) {
                    patternSet.combine(patternSets[indexes[i]]);
                }
                patternSetCombination.push(patternSet);
            }
            this.patterns.setValue(i, patternSetCombination);
        }
    }
    createClearWinTimer() {
        this.clearWinTimer = new Timer(3000, 1);
        this.clearWinTimer.onComplete(this.handleClearWin, this);
    }
    createUpdateWinTimer() {
        this.updateWinTimer = new Timer(2000, 1);
        this.updateWinTimer.onComplete(this.handleUpdateWin, this);
    }
    reset() {
        this.updateWinTimer.stop();
        this.clearWinTimer.stop();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.handlePurchaseSpin);
    }
    handlePurchaseSpin() {
        if (this.model.isSpin() && !this.model.isChooseCell() && !this.model.isStakeToSpin() && !this.model.getSpinResultResolving()) {
            this.dispatchGame(SlingoEvent.AVAILABLE_WIN_START);
            let winAvailable = this.updateAvailableWins();
            if (winAvailable) {
                this.startWinTimer(100);
            }
        }
    }
    handleUpdateWin() {
        this.nextWin();
        this.updateWin();
    }
    nextWin() {
        let availableWins = this.model.getAvailableWins();
        let index = this.model.getAvailableWinIndex();
        let newIndex = index + 1;
        if (newIndex >= availableWins?.length) {
            this.updateAvailableWins();
            newIndex = 0;
        }
        this.model.setAvailableWinIndex(newIndex);
    }
    startWinTimer(value) {
        this.updateWinTimer.reset(value, 1);
        this.updateWinTimer.start();
    }
    updateWin() {
        if (this.operatorConfig.isYouCouldWinEnabled()) {
            this.dispatchGame(SlingoEvent.AVAILABLE_WIN);
            this.clearWinTimer.start();
        }
    }
    handleClearWin() {
        this.clearWinTimer.stop();
        this.dispatchGame(SlingoEvent.AVAILABLE_WIN_CLEAR);
        this.startWinTimer(1000);
    }
    updateAvailableWins() {
        let payoutConfig = this.model.getPayoutConfig();
        let payouts = payoutConfig.getPatternPayouts();
        let maxPatterns = payouts.length;
        let stake = this.model.getCurrentStake();
        let matchedPatterns = this.model.getCurrentMatchedPatterns();
        let numberOfPatterns = matchedPatterns + 1;
        let winsByKey = new Dictionary();
        for (let i = numberOfPatterns; i <= maxPatterns; i++) {
            let winAmount = this.getWinAmount(stake, payouts[i - 1]);
            if (winAmount > 0) {
                let patternCombination = this.patterns.getValue(i);
                for (let j = 0; j < patternCombination.length; j++) {
                    let patternSet = patternCombination[j];
                    let availableWin = this.createAvailableWin(patternSet, i, winAmount);
                    if (availableWin) {
                        winsByKey.setValue(availableWin.getKey(), availableWin);
                    }
                }
            }
        }
        const availableWinsArray = winsByKey.getValues();
        const pickedWins = ArrayUtil.groupBy(availableWinsArray, (elem => elem.getWinAmount()));
        const pickedWinKeys = Object.keys(pickedWins).filter(key => pickedWins.hasOwnProperty(key));
        const pickedWinsArray = pickedWinKeys.map(key => pickedWins[key][Math.floor(Math.random() * pickedWins[key].length)]);
        this.model.setAvailableWins(pickedWinsArray.sort((a, b) => b.getWinAmount() - a.getWinAmount()));
        this.model.setAvailableWinIndex(-1);
        return pickedWinsArray.length > 0;
    }
    getWinAmount(base, prize) {
        return base * prize;
    }
    createAvailableWin(patternSet, numberOfPatterns, winAmount) {
        let rows = patternSet.getRows();
        let columns = patternSet.getColumns();
        let ticket = this.model.getTicket();
        let cellsRemaining = [];
        let cellNumbers = [];
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                let ticketCell = ticket.getCellAt(row, column);
                let patternCell = patternSet.getCell(row, column);
                if (patternCell.isActive() && !ticketCell.isDabbed()) {
                    cellsRemaining.push(ticketCell);
                    cellNumbers.push(ticketCell.getNumber());
                }
            }
        }
        let win;
        let numberOfCells = cellsRemaining.length;
        if (numberOfCells > 0 && numberOfCells <= 5) {
            let key = this.createKey(cellNumbers);
            win = new AvailableWinData(key, numberOfPatterns, cellsRemaining, winAmount);
        }
        return win;
    }
    createKey(cellNumbers) {
        return cellNumbers.sort(this.sortNumbers).toString();
    }
    sortWins(a, b) {
        let difference = b.getNumberOfPatterns() - a.getNumberOfPatterns();
        if (difference == 0) {
            difference = a.getNumberOfCells() - b.getNumberOfCells();
        }
        return difference;
    }
    sortNumbers(a, b) {
        return a - b;
    }
}
//# sourceMappingURL=WinController.js.map